"use client";

import React from "react";
import "./single-range-slider.css";

function interpolateSpectrumColor(ratio) {
  const start = { r: 0, g: 174, b: 239 }; // #00aeef
  const end = { r: 236, g: 0, b: 140 };   // #ec008c
  const clamped = Math.max(0, Math.min(1, ratio));
  const r = Math.round(start.r + (end.r - start.r) * clamped);
  const g = Math.round(start.g + (end.g - start.g) * clamped);
  const b = Math.round(start.b + (end.b - start.b) * clamped);
  return `rgb(${r}, ${g}, ${b})`;
}

export default function SingleRangeSlider({
  label,
  leftLabel,
  rightLabel,
  value = 3,
  onChange,
  min = 1,
  max = 5,
  disabled = false,
  irrelevant = false,
  onIrrelevantToggle,
  showOptOut,
  showTicks = true,
}) {
  const normalizedValue = Number(value) || min;
  const ratio = (normalizedValue - min) / (max - min);
  const percentage = Math.max(0, Math.min(100, ratio * 100));
  const thumbColor = interpolateSpectrumColor(ratio);
  // Native <input type="range"> insets the thumb so it stays inside the
  // input's bounds: thumb center sits at `half_thumb_width + ratio *
  // (track_width − thumb_width)`. The gradient stop, by contrast, is
  // computed as a flat percentage of the input width, so the colored
  // portion runs slightly past the thumb at low values and falls short at
  // high ones. Compensating the stop by `(1 − 2 * ratio) * half_thumb_width`
  // realigns the color transition with the thumb's center, which makes the
  // line terminate cleanly at the thumb instead of bleeding past it. Thumb
  // is 20px wide → half-width = 10px.
  const trackStop = `calc(${percentage}% + ${(1 - 2 * ratio) * 10}px)`;
  const trackColor = `linear-gradient(to right, #00aeef 0%, ${thumbColor} ${trackStop}, #e5e7eb ${trackStop}, #e5e7eb 100%)`;

  return (
    <div className={`single-slider ${irrelevant ? "single-slider--optedOut" : ""}`}>
      <div className="single-slider__header">
        <div className="single-slider__heading">
          <h4 className="single-slider__label">{label}</h4>
        </div>

        {showOptOut && onIrrelevantToggle && (
          <label className="single-slider__optout">
            <input
              type="checkbox"
              checked={irrelevant}
              onChange={onIrrelevantToggle}
              className="single-slider__checkbox"
            />
            <span>Opt-out</span>
          </label>
        )}
      </div>

      <div className={`single-slider__content ${irrelevant ? "single-slider__content--disabled" : ""}`}>
        <span className="single-slider__side single-slider__side--left">{leftLabel}</span>

        <div className="single-slider__trackContainer">
          <input
            type="range"
            min={min}
            max={max}
            value={normalizedValue}
            onChange={(e) => onChange?.(e.target.value)}
            disabled={disabled || irrelevant}
            className="single-slider__input"
            style={{
              "--slider-track": trackColor,
              "--slider-thumb": thumbColor,
            }}
          />

          {showTicks && (
            <div className="single-slider__ticks">
              {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
                <span key={n} className="single-slider__tick">{n}</span>
              ))}
            </div>
          )}
        </div>

        <span className="single-slider__side single-slider__side--right">{rightLabel}</span>
      </div>
    </div>
  );
}
